import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  DatePicker,
  Table,
  Typography,
  Space,
  Popconfirm,
  App,
  Select,
  Flex,
  Grid,
  message,
  Radio,
} from "antd";
import InputNumberField from "../../components/Input/InputNumber";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  fetchReceiptCode,
  fetchDueVouchers,
  fetchDueEmis,
  fetchCustomerBalance,
  saveReceipt,
  fetchReceipts,
  fetchDirectExpenseAccounts,
  fetchDuePurchaseReturns,   // ← new thunk to add
  fetchSupplierBalance,      // ← new thunk to add
} from "./customerReceiptThunk";
import {
  fetchCustomers,
  fetchLoadIntoAccs,
  fetchLoadIntoAccsExcludeCheque,
  fetchTagGroupFields,
  fetchTagGroups,
  fetchSuppliers,            // ← new
} from "../../store/commonThunks/lookups";
import SearchListAutoComplete from "../../components/SearchListAutoComplete";
import { useNavigate, useParams } from "react-router-dom";
import {
  dateTimeFormat,
  formatToUtc,
  formatToUtcString,
  getAccessType,
  getDateTimeFromSelectedDate,
} from "../../lib/functions";
import {
  resetReceipts,
  setEntryDate,
  setIsEditReceipt,
  setPaymentCode,
  setSelectedReceipt,
} from "./customerReceiptSlice";
import { getCurrencySymbol } from "../../lib/currency";
import AddNewModal from "../../components/DropDown/AddNewModal";
import TagCreateForm from "../Settings/Tags/components/TagCreateForm";
import InputField from "../../components/Input/Input";
import useMigrationDate from "../../hooks/useMigrationDate";

const { Title } = Typography;
const { useBreakpoint } = Grid;
const { TextArea } = Input;

const CustomerReceipts = ({ authInfo }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const { modal } = App.useApp();
  const [form] = Form.useForm();
  const symbol = getCurrencySymbol(authInfo?.currency || "AED");

  // ✅ receipt mode toggle
  const [receiptMode, setReceiptMode] = useState("customer_receipt");
  const isSupplierReceiptMode = receiptMode === "supplier_receipt";

  const [rows, setRows] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [tagGroupList, setTagGroupList] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagModal, setTagModal] = useState(false);
  const [totalReceived, setTotalReceived] = useState(0);
  let [access_type, access_type_set] = useState("");

  const {
    entryDate,
    receiptCode,
    dueVouchers,
    dueEmis,
    balances,
    duePurchaseReturns,
    supplierBalances,
    selectedReceipt,
    isEditReceipt,
    directExpense,
  } = useSelector((state) => state.customerReceipts);

  const { customers, loadIntoAccs, loadIntoAccounts, suppliers } = useSelector((s) => s.lookups);

  useEffect(() => {
    const access = getAccessType("debtor_receipt");
    access_type_set(access);
  }, []);

  useEffect(() => {
    if (!id) dispatch(fetchReceiptCode());
  }, [dispatch, id]);

  useEffect(() => {
    dispatch(fetchCustomers());
    dispatch(fetchSuppliers());
    dispatch(fetchDirectExpenseAccounts());
    dispatch(fetchLoadIntoAccs());
    dispatch(fetchLoadIntoAccsExcludeCheque());
    dispatch(fetchTagGroups())
      .unwrap()
      .then((groups) =>
        Promise.all(groups.map((g) => dispatch(fetchTagGroupFields(g.id)).unwrap()))
      )
      .then((allFields) => {
        const combined = allFields.flat().map((item) => ({
          ...item.raw,
          combinedField: item.display_text,
          id: item.id,
        }));
        setTagGroupList(combined);
      });
    setRows([createEmptyRow(null)]);
  }, [dispatch]);

  // ─── FIFO distribution ───────────────────────────────────────────────────
  const distributePayment = (amount) => {
    setTotalReceived(amount);
    let remaining = Number(amount);
    const newRows = rows.map((row) => {
      const due = Number(row.voucher_details?.due || 0) +
                  (isEditReceipt ? Number(row.original_amount || 0) : 0);
      const allocated = Math.min(remaining, due);
      remaining = Math.max(0, remaining - allocated);
      return { ...row, amount: allocated };
    });
    setRows(newRows);
  };

  const redistributeAfterSelect = (updatedRows) => {
    if (totalReceived <= 0) { setRows(updatedRows); return; }
    let remaining = Number(totalReceived);
    const redistributed = updatedRows.map((row) => {
      const due = Number(row.voucher_details?.due || 0) +
                  (isEditReceipt ? Number(row.original_amount || 0) : 0);
      const allocated = Math.min(remaining, due);
      remaining = Math.max(0, remaining - allocated);
      return { ...row, amount: allocated };
    });
    setRows(redistributed);
  };

  // ─── Edit mode ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (id && !selectedReceipt) {
      dispatch(fetchReceipts())
        .unwrap()
        .then((list) => {
          const found = list.find((r) => r.rcv_id === id);
          if (found) {
            dispatch(setSelectedReceipt(found));
            dispatch(setIsEditReceipt(true));
            if (found.receipt_type === "supplier_receipt") {
              setReceiptMode("supplier_receipt");
            }
          }
        });
    }
  }, [id, selectedReceipt, dispatch]);

  useEffect(() => {
    if (id && selectedReceipt) {
      dispatch(fetchCustomerBalance(selectedReceipt.acc_id));
      dispatch(setIsEditReceipt(true));
      dispatch(fetchDueVouchers(selectedReceipt.acc_id));
      dispatch(setPaymentCode(selectedReceipt.rcv_code));
      if (selectedReceipt.receipt_type === "supplier_receipt") {
        setReceiptMode("supplier_receipt");
      }
    }
  }, [id, selectedReceipt, dispatch]);

  useEffect(() => {
    if (isEditReceipt && selectedReceipt) {
      form.setFieldsValue({
        rcv_date: dayjs(selectedReceipt.created_date),
        description: selectedReceipt.narration,
        tags: selectedReceipt?.tag_field_id || [],
      });
      setSelectedTags(selectedReceipt?.tag_field_id);

      const isSupplier = selectedReceipt.receipt_type === "supplier_receipt";

      if (isSupplier) {
        setReceiptMode("supplier_receipt");
        const first = selectedReceipt.details?.[0];
        if (first) {
          setSelectedSupplier({ acc_id: first.from_acc_id, acc_name: first.from_acc_name });
          dispatch(fetchDuePurchaseReturns(first.from_acc_id));
        }
      } else {
        setSelectedCustomer({ acc_name: selectedReceipt.acc_name, acc_id: selectedReceipt.acc_id });
        dispatch(fetchDueVouchers(selectedReceipt.acc_id));
      }

      setRows(
        (selectedReceipt.details || []).map((d) => ({
          key: Date.now() + Math.random(),
          into_acc_id: d.into_acc_id,
          into_acc_name: d.into_acc_name,
          into_acc_type_id: d.into_acc_type_id,
          from_acc_id: d.from_acc_id,
          from_acc_name: d.from_acc_name,
          from_customer: { acc_id: d.from_acc_id, acc_name: d.from_acc_name },
          voucher_no: d.voucher_no,
          emi_id: d.emi_id,
          emi_no: d.emi_no,
          amount: d.rcv_amount,
          original_amount: d.rcv_amount,
          discount: d.discount_amount,
          cheque_number: d.cheque_number || "",
          pdc_date: d.pdc_date ? dayjs(d.pdc_date) : "",
          expected_bank_acc_id: d.expected_bank_acc_id || null,
          voucher_details: null,
        }))
      );
    }
  }, [isEditReceipt, selectedReceipt, form]);

  useEffect(() => {
    if (!isEditReceipt || !selectedReceipt) return;
    const customerVouchers = dueVouchers[selectedReceipt.acc_id];
    if (!customerVouchers || customerVouchers.length === 0) return;
    setRows((prevRows) =>
      prevRows.map((row) => {
        const matched = customerVouchers.find((v) => v.voucher_no === row.voucher_no);
        return { ...row, voucher_details: matched || { no_due: true } };
      })
    );
  }, [dueVouchers, selectedReceipt, isEditReceipt]);

  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    if (customer?.acc_id) {
      dispatch(fetchDueVouchers(customer.acc_id));
      dispatch(fetchDueEmis(customer.acc_id));
      dispatch(fetchCustomerBalance(customer.acc_id));
    }
  };

  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    if (supplier?.acc_id) {
      dispatch(fetchDuePurchaseReturns(supplier.acc_id));
    }
  };

  const createEmptyRow = (entity) => ({
    key: Date.now() + Math.random(),
    customerKey: entity?.acc_id || "none",
    from_customer: entity,
    into_acc_name: "",
    into_acc_id: 0,
    into_acc_type_id: "",
    voucher_no: "",
    voucher_details: null,
    emi_id: null,
    emi_no: "",
    amount: 0,
    discount: null,
    cheque_number: "",
    expected_bank_acc_id: null,
  });

  const handleAddRow = () => setRows([...rows, createEmptyRow(isSupplierReceiptMode ? selectedSupplier : selectedCustomer)]);
  const handleRemoveRow = (key) => setRows(rows.filter((row) => row.key !== key));

  const handleCancel = () => {
    dispatch(setSelectedReceipt(null));
    dispatch(setIsEditReceipt(false));
    navigate("/accounts/debitor-receipts");
  };

  // ─── Save ─────────────────────────────────────────────────────────────────
  const onFinish = async (values) => {
    try {
        let finalEntryDate = entryDate;
  if (!dayjs(entryDate).isSame(dayjs(), "day")) {
    finalEntryDate = getDateTimeFromSelectedDate(entryDate);
  }
      const totalAmount = rows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
      const totalDiscount = rows.reduce((sum, r) => sum + Number(r.discount || 0), 0);

      if (isSupplierReceiptMode) {
        if (!selectedSupplier?.acc_id) {
          modal.error({ title: "Please select a supplier before saving." });
          return;
        }
        for (let r of rows) {
          if (!r.voucher_no) {
            modal.error({ title: "Please select a Purchase Return voucher before saving." });
            return;
          }
          if (r.amount == null || r.amount <= 0) {
            modal.error({ title: "Please enter a valid Amount before saving." });
            return;
          }
          const due = Number(r.voucher_details?.due || 0) +
                      (isEditReceipt ? Number(r.original_amount || 0) : 0);
          if (Number(r.amount) > due + 0.01) {
            modal.error({
              title: "Amount Exceeded",
              content: `Amount for ${r.voucher_no} cannot exceed pending amount of ${due.toFixed(2)}`,
            });
            return;
          }
          if (!r.into_acc_name) {
            modal.error({ title: "Please select Account (cash/bank) to receive into." });
            return;
          }
        }


        const rcvPayload = {
          rcv_id: isEditReceipt ? selectedReceipt.rcv_id : 0,
          action: isEditReceipt ? "update" : "create",
          rcv_code: receiptCode,
   created_date: formatToUtc(finalEntryDate),
          narration: values.description || "",
          rcv_total: totalAmount,
          discount_total: totalDiscount,
          tax_total: 0,
          receipt_type: "supplier_receipt",
        };

        if (selectedTags.length > 0) rcvPayload.tag_field_id = selectedTags;

        const payload = {
          rcv: rcvPayload,
          rcvDetail: rows.map((r) => ({
            from_acc_id: selectedSupplier.acc_id,     // money comes FROM supplier
            from_acc_name: selectedSupplier.acc_name,
            into_acc_id: r.into_acc_id || 0,          // money goes INTO cash/bank
            into_acc_name: r.into_acc_name || "",
            into_acc_type_id: r.into_acc_type_id || "",
            voucher_no: r.voucher_no || "",
            emi_id: 0,
            emi_no: "",
            rcv_amount: Number(r.amount) || 0,
            discount_amount: Number(r.discount) || 0,
            tax_amount: 0,
            direct_income_id: directExpense?.acc_id,
            current_liability_id: 0,
            rcv_total: (Number(r.amount) || 0) - (Number(r.discount) || 0),
            amount: Number(r.amount) || 0,
            cheque_number: r.cheque_number || null,
            pdc_date: r.pdc_date ? formatToUtcString(r.pdc_date) : formatToUtcString(dayjs()),
            expected_bank_acc_id: r.expected_bank_acc_id || null,
          })),
        };

        const res = await dispatch(saveReceipt(payload)).unwrap();
        modal.success({ title: res.msg || "Supplier receipt saved successfully" });

      } else {
        // ── Customer Receipt validation (existing logic) ──
        const customer = rows[0]?.from_customer;
        if (!customer?.acc_id) {
          modal.error({ title: "Please select a customer before saving." });
          return;
        }
        const allSameCustomer = rows.every((r) => r.from_customer?.acc_id === customer?.acc_id);
        if (!allSameCustomer) {
          modal.error({ title: "All rows must belong to the same customer" });
          return;
        }
        for (let r of rows) {
          const isOBRow = r.voucher_no === "__OB__";
          if (!r.voucher_no && !isOBRow) {
            modal.error({ title: "Please select Voucher before saving." });
            return;
          }
          if (r.amount == null || r.amount <= 0) {
            modal.error({ title: "Please enter a valid Amount before saving." });
            return;
          }
          if (!isOBRow) {
            const voucherDue = Number(r.voucher_details?.due || 0);
            const originalAmount = isEditReceipt ? Number(r.original_amount || 0) : 0;
            const availableDue = voucherDue + originalAmount;
            if (Number(r.amount) > availableDue + 0.01) {
              modal.error({
                title: "Amount Exceeded",
                content: `Amount for voucher ${r.voucher_no} cannot exceed due amount of ${symbol}${availableDue.toFixed(2)}`,
              });
              return;
            }
          }
          if (!r.into_acc_name) {
            modal.error({ title: "Please select Account before saving." });
            return;
          }
        }
console.log('values.rcv_date',values.rcv_date);
        const prevDue = balances[customer.acc_id]?.total_balance || 0;
        const rcvPayload = {
          rcv_id: isEditReceipt ? selectedReceipt.rcv_id : 0,
          action: isEditReceipt ? "update" : "create",
          rcv_code: receiptCode,
created_date: formatToUtc(finalEntryDate),
          narration: values.description || "",
          rcv_total: totalAmount,
          discount_total: totalDiscount,
          tax_total: 0,
          receipt_type: "customer_receipt",
        };

        console.log('rcvPayload:::', rcvPayload);
        if (selectedTags.length > 0) rcvPayload.tag_field_id = selectedTags;

        const payload = {
          contact_no: customer?.contact_no || "",
          is_smg: "yes",
          msg: `Dear ${customer.acc_name}, Your payment amount is ${totalAmount.toFixed(2)}, Your Previous Due is ${prevDue.toFixed(2)} & Current Due is ${(prevDue - totalAmount).toFixed(2)}`,
          rcv: rcvPayload,
          rcvDetail: rows.map((r) => ({
            into_acc_id: r.into_acc_id || 0,
            into_acc_name: r.into_acc_name || "",
            into_acc_type_id: r.into_acc_type_id || "",
            from_acc_id: r.from_customer?.acc_id || 0,
            from_acc_name: r.from_customer?.acc_name || "",
            voucher_no: r.voucher_no === "__OB__" ? null : (r.voucher_no || ""),
            emi_id: r.emi_id || 0,
            emi_no: r.emi_no || "",
            rcv_amount: Number(r.amount) || 0,
            discount_amount: Number(r.discount) || 0,
            tax_amount: 0,
            direct_income_id: directExpense?.acc_id,
            current_liability_id: 0,
            rcv_total: (Number(r.amount) || 0) - (Number(r.discount) || 0),
            amount: Number(r.amount) || 0,
            cheque_number: r.cheque_number || null,
            pdc_date: r.pdc_date ? formatToUtcString(r.pdc_date) : formatToUtcString(dayjs()),
            expected_bank_acc_id: r.expected_bank_acc_id || null,
          })),
        };

        const res = await dispatch(saveReceipt(payload)).unwrap();
        modal.success({ title: res.msg || "Receipt saved successfully" });
      }

      dispatch(setSelectedReceipt(null));
      dispatch(setIsEditReceipt(false));
      form.resetFields();
      setRows([]);
      setTotalReceived(0);
      setSelectedCustomer(null);
      setSelectedSupplier(null);
      navigate("/accounts/debitor-receipts");
    } catch (err) {
      modal.error({ title: err.message || "Failed to save receipt" });
    }
  };

  // ─── Dynamic columns ──────────────────────────────────────────────────────
  const pdcColumn = rows.some((r) => r.into_acc_name?.toLowerCase() === "pdc")
    ? [{
        title: "PDC Date",
        dataIndex: "pdc_date",
        width: 160,
        render: (_, record, index) =>
          record.into_acc_name?.toLowerCase() === "pdc" ? (
            <DatePicker
              style={{ width: "100%" }}
              value={record.pdc_date ? dayjs(record.pdc_date) : null}
              onChange={(d) => { const newRows = [...rows]; newRows[index].pdc_date = d ? d.toISOString() : null; setRows(newRows); }}
              format={dateTimeFormat}
            />
          ) : "-",
      }]
    : [];

  const chequeNumberColumn = rows.some((r) => r.into_acc_type_id?.toLowerCase() === "cheque")
    ? [{
        title: "Cheque Number",
        dataIndex: "cheque_number",
        width: 160,
        render: (_, record, index) => (
          <InputField
            placeholder="Enter Cheque Number"
            value={record.cheque_number || ""}
            onChange={(e) => { const newRows = [...rows]; newRows[index].cheque_number = e.target.value; setRows(newRows); }}
          />
        ),
      }]
    : [];

  const expectedBankColumn = rows.some((r) => r.into_acc_type_id?.toLowerCase() === "cheque")
    ? [{
        title: "Expected Bank",
        dataIndex: "expected_bank_acc_id",
        width: 180,
        render: (_, record, index) => (
          <Select
            allowClear
            placeholder="Expected Bank (optional)"
            style={{ width: "100%" }}
            value={record.expected_bank_acc_id || undefined}
            options={(loadIntoAccounts || []).map((acc) => ({
              label: acc.acc_name,
              value: acc.acc_id,
            }))}
            onChange={(v) => { const newRows = [...rows]; newRows[index].expected_bank_acc_id = v || null; setRows(newRows); }}
          />
        ),
      }]
    : [];

  const receivedIntoColumn = {
    title: "Received Into",
    dataIndex: "into_acc_name",
    render: (_, record, index) => (
      <SearchListAutoComplete
        entityType="accounts"
        style={{ width: "100%" }}
        optionsList={(loadIntoAccs || []).map((acc) => ({
          sale_id: acc.acc_id,
          display_text: acc.display_text || acc.acc_name,
          raw: acc,
        }))}
        onSelect={(obj) => {
          const newRows = [...rows];
          newRows[index].into_acc_name = obj?.raw?.acc_name || "";
          newRows[index].into_acc_id = obj?.raw?.acc_id || 0;
          newRows[index].into_acc_type_id = obj?.raw?.acc_type_id || "";
          setRows(newRows);
        }}
        value={record.into_acc_name ? { value: record.into_acc_name, label: record.into_acc_name } : null}
        placeholder="Cash / Bank / Cheque"
        showAddNew={false}
      />
    ),
  };

  // ─── Supplier Receipt columns ──────────────────────────────────────────────
  const supplierReceiptColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 50 },
    {
      title: "Supplier",
      render: (_, record, index) => (
        <SearchListAutoComplete
          entityType="supplier"
          style={{ width: "100%" }}
          optionsList={(suppliers || []).map((s) => ({
            sale_id: s.acc_id,
            display_text: [s.acc_name, s.contact_no, s.institution_name].filter(Boolean).join(" - "),
            raw: s,
          }))}
          value={selectedSupplier ? { value: selectedSupplier.acc_name, label: selectedSupplier.acc_name } : null}
          allowClear
          onClear={() => { setSelectedSupplier(null); setRows([createEmptyRow(null)]); }}
          onSelect={(obj) => {
            const newSupplier = obj?.raw;
            if (!newSupplier?.acc_id) return;
            const currentSupplier = selectedSupplier;
            if (currentSupplier && currentSupplier.acc_id !== newSupplier.acc_id) {
              setSelectedSupplier(newSupplier);
              setRows([createEmptyRow(newSupplier)]);
              dispatch(fetchDuePurchaseReturns(newSupplier.acc_id));
              return;
            }
            setSelectedSupplier(newSupplier);
            setRows([createEmptyRow(newSupplier)]);
            handleSupplierSelect(newSupplier);
          }}
          placeholder="Supplier"
          showAddNew={false}
        />
      ),
    },
    {
      title: "Purchase Return Voucher",
      dataIndex: "voucher_no",
      render: (_, record, index) => {
        const purchaseReturns = selectedSupplier?.acc_id
          ? duePurchaseReturns?.[selectedSupplier.acc_id] || []
          : [];
        const allOptions = purchaseReturns.map((r) => ({
          sale_id: r.voucher_no,
          display_text: r.display_text,
          raw: { ...r, due: r.due },
        }));
        return (
          <div>
            <SearchListAutoComplete
              entityType="vouchers"
              style={{ width: "100%" }}
              optionsList={allOptions}
              onSelect={(obj) => {
                const newRows = [...rows];
                newRows[index].voucher_no = obj?.raw?.voucher_no || "";
                newRows[index].voucher_details = obj?.raw || null;
                redistributeAfterSelect(newRows);
              }}
              value={record.voucher_no ? { value: record.voucher_no, label: record.voucher_no } : null}
              placeholder="Purchase Return Voucher"
              showAddNew={false}
            />
            {record.voucher_details && !record.voucher_details.no_due && (
              <div style={{ fontSize: 12, marginTop: 4 }}>
                <span style={{ color: "orange", marginRight: 8 }}>
                  Pending: {Number(record.voucher_details.due || 0).toFixed(2)}
                </span>
                <span style={{ color: "green", marginRight: 8 }}>
                  Already Received: {(Number(record.voucher_details.paid_amount || 0) + Number(record.voucher_details.already_received || 0)).toFixed(2)}
                </span>
                <span>Total Return: {Number(record.voucher_details.total_amount || 0).toFixed(2)}</span>
              </div>
            )}
          </div>
        );
      },
    },
    receivedIntoColumn,
    ...pdcColumn,
    ...chequeNumberColumn,
    ...expectedBankColumn,
    {
      title: "Amount",
      dataIndex: "amount",
      render: (_, record, index) => (
        <InputNumberField min={0} value={record.amount} stringMode step={1} precision={2} style={{ width: "100%" }}
         onClick={() => {
            const newRows = [...rows];

            if (newRows[index].amount === 0) {
              newRows[index].amount = "";
              setRows(newRows);
            }
          }}
          onChange={(val) => { const newRows = [...rows]; newRows[index].amount = val || 0; setRows(newRows); }} />
      ),
    },
    { title: "Actions", render: (_, record) => (
      <Popconfirm title="Remove this row?" onConfirm={() => handleRemoveRow(record.key)}>
        <Button type="link" danger>Delete</Button>
      </Popconfirm>
    )},
  ];

  // ─── Customer Receipt columns (existing) ──────────────────────────────────
  const emiColumn = {
    title: "EMI No",
    dataIndex: "emi_no",
    render: (_, record, index) => (
      <div>
        <SearchListAutoComplete
          entityType="vouchers"
          style={{ width: "100%" }}
          optionsList={dueEmis.map((e) => ({ sale_id: e.emi_id, display_text: e.emi_no, raw: e }))}
          onSelect={(obj) => {
            const newRows = [...rows];
            newRows[index].emi_id = obj?.sale_id || null;
            newRows[index].emi_no = obj?.display_text || "";
            newRows[index].emi_details = obj?.raw || null;
            redistributeAfterSelect(newRows);
          }}
          placeholder="Select EMI"
          showAddNew={false}
        />
        {record.emi_details && (
          <div style={{ fontSize: 12, marginTop: 4 }}>
            <span style={{ color: "red", marginRight: 8 }}>EMI Due {record.emi_details.due}</span>
          </div>
        )}
      </div>
    ),
  };

  const customerReceiptColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 50 },
    {
      title: "From Customer",
      dataIndex: "from_customer",
      render: (_, record, index) => (
        <SearchListAutoComplete
          entityType="customer"
          style={{ width: "100%" }}
          optionsList={customers.map((c) => ({ sale_id: c.acc_id, display_text: c.acc_name, raw: c }))}
          value={selectedCustomer ? { value: selectedCustomer.acc_name, label: selectedCustomer.acc_name } : null}
          allowClear
          onClear={() => { setSelectedCustomer(null); setTotalReceived(0); setRows([createEmptyRow(null)]); }}
          onSelect={(obj) => {
            const newCustomer = obj?.raw;
            if (!newCustomer?.acc_id) return;
            const currentCustomer = rows.find((r) => r.from_customer?.acc_id)?.from_customer;
            if (currentCustomer && currentCustomer.acc_id !== newCustomer.acc_id) {
              setSelectedCustomer(newCustomer);
              setTotalReceived(0);
              setRows([createEmptyRow(newCustomer)]);
              dispatch(fetchDueVouchers(newCustomer.acc_id));
              dispatch(fetchDueEmis(newCustomer.acc_id));
              dispatch(fetchCustomerBalance(newCustomer.acc_id));
              return;
            }
            setSelectedCustomer(newCustomer);
            setRows([createEmptyRow(newCustomer)]);
            handleCustomerSelect(newCustomer);
          }}
          placeholder="Customer"
          showAddNew={false}
        />
      ),
    },
    {
      title: "Total Due",
      dataIndex: "total_due",
      render: () => selectedCustomer?.acc_id
        ? Number(balances[selectedCustomer.acc_id]?.total_balance || 0).toFixed(2)
        : 0,
    },
    {
      title: "Voucher",
      dataIndex: "voucher_no",
      render: (_, record, index) => {
        const customerVouchers = selectedCustomer?.acc_id ? dueVouchers[selectedCustomer.acc_id] || [] : [];
        const openingBalance = balances[selectedCustomer?.acc_id]?.opening_balance || 0;
        const obOption = openingBalance > 0
          ? [{ sale_id: "__OB__", display_text: `Opening Balance — Due: ${Number(openingBalance).toFixed(2)}`,
              raw: { voucher_no: "__OB__", total_amount: openingBalance, paid: 0, due: openingBalance, discountAmount: 0, is_opening_balance: true } }]
          : [];
        const allOptions = [...obOption, ...customerVouchers.map((v) => ({ sale_id: v.voucher_no, display_text: v.display_text, raw: v }))];
        const isOBRow = record.voucher_no === "__OB__";
        return (
          <div>
            <SearchListAutoComplete
              data-testid="voucher-select"
              entityType="vouchers"
              style={{ width: "100%" }}
              optionsList={allOptions}
              value={record.voucher_no ? { value: isOBRow ? "Opening Balance" : record.voucher_no, label: isOBRow ? "Opening Balance" : record.voucher_no } : null}
              onSelect={(obj) => {
                const newRows = [...rows];
                newRows[index].voucher_no = obj?.raw?.voucher_no || "";
                newRows[index].voucher_details = obj?.raw || null;
                redistributeAfterSelect(newRows);
              }}
              placeholder="Voucher"
              showAddNew={false}
            />
            {record.voucher_details && (
              isOBRow || record.voucher_details.is_opening_balance ? (
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  <span style={{ color: "red", marginRight: 8 }}>Due: {Number(record.voucher_details.due || 0).toFixed(2)}</span>
                  <span style={{ color: "gray" }}>Opening Balance</span>
                </div>
              ) : record.voucher_details.no_due ? (
                <div style={{ fontSize: 12, marginTop: 4, color: "gray" }}>No Due</div>
              ) : (
                <div data-testid={`voucher-details-${index}`} style={{ fontSize: 12, marginTop: 4 }}>
                  <span style={{ color: "red", marginRight: 8 }}>
                    Due: {(Number(record.voucher_details.due || 0) + (isEditReceipt ? Number(record.original_amount || 0) : 0)).toFixed(2)}
                  </span>
                  <span style={{ color: "brown", marginRight: 8 }}>Discount {Number(record.voucher_details.discountAmount).toFixed(2)}</span>
                  <span style={{ color: "green", marginRight: 8 }}>Paid {Number(record.voucher_details.paid).toFixed(2)}</span>
                  <span>Bill {Number(record.voucher_details.total_amount).toFixed(2)}</span>
                </div>
              )
            )}
          </div>
        );
      },
    },
    ...(dueEmis.length > 0 ? [emiColumn] : []),
    receivedIntoColumn,
    ...pdcColumn,
    ...chequeNumberColumn,
    ...expectedBankColumn,
    {
      title: "Amount",
      dataIndex: "amount",
      render: (_, record, index) => (
        <InputNumberField 
          data-testid="amount-input" 
          min={0} 
          value={record.amount} 
          stringMode 
          step={1} 
          precision={2} 
          style={{ width: "100%" }}
          onClick={() => {
          const newRows = [...rows];

          if (newRows[index].amount === 0) {
            newRows[index].amount = "";
            setRows(newRows);
          }
        }}  
        onChange={(val) => { const newRows = [...rows]; newRows[index].amount = val || 0; setRows(newRows); }} />
      ),
    },
    {
      title: "Discount",
      dataIndex: "discount",
      render: (_, record, index) => (
        <InputNumberField 
          min={0} 
          value={record.discount} 
          step={1} 
          precision={2} 
          stringMode 
          style={{ width: "100%" }}
          onClick={() => {
            const newRows = [...rows];

            if (newRows[index].discount === 0) {
              newRows[index].discount = "";
              setRows(newRows);
            }
          }}  
        onChange={(val) => { const newRows = [...rows]; newRows[index].discount = val || 0; setRows(newRows); }} />
      ),
    },
    { title: "Actions", render: (_, record) => (
      <Popconfirm title="Remove this row?" onConfirm={() => handleRemoveRow(record.key)}>
        <Button type="link" danger>Delete</Button>
      </Popconfirm>
    )},
  ];

  const totalAmount = rows.reduce((sum, r) => sum + Number(r.amount), 0);
  const totalDiscount = rows.reduce((sum, r) => sum + Number(r.discount), 0);
  const { disabledDate } = useMigrationDate();

  return (
    <div style={{ padding: 16 }} data-testid="customer-receipt-form-page">
      <Title level={3} data-testid="page-title">
        {isSupplierReceiptMode ? "Supplier Receipt" : "Customer Receipts"}
      </Title>

      {/* ✅ Receipt mode toggle */}
      {!isEditReceipt && (
        <div style={{ marginBottom: 20 }}>
          <Radio.Group
            value={receiptMode}
            onChange={(e) => {
              setReceiptMode(e.target.value);
              setRows([createEmptyRow(null)]);
              setSelectedCustomer(null);
              setSelectedSupplier(null);
              setTotalReceived(0);
            }}
            buttonStyle="solid"
          >
            <Radio.Button value="customer_receipt">Customer Receipt</Radio.Button>
            <Radio.Button value="supplier_receipt">Supplier Receipt</Radio.Button>
          </Radio.Group>
        </div>
      )}

      {/* ✅ Supplier receipt info banner */}
      {isSupplierReceiptMode && (
        <div style={{ background: "#e6f7ff", border: "1px solid #91d5ff", borderRadius: 6, padding: "8px 16px", marginBottom: 16, fontSize: 13, color: "#0050b3" }}>
          Supplier Receipt mode — select a supplier to see pending purchase return amounts. Money flows IN to your cash/bank FROM the supplier.
        </div>
      )}

      <Form
        form={form}
        layout="horizontal"
        style={{ width: "100%" }}
        colon={false}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 18 }}
        initialValues={{ rcv_date: entryDate ? dayjs(entryDate) : null }}
        onFinish={onFinish}
        data-testid="receipt-form"
      >
        <Flex gap={!screens.md || !screens.xl ? 10 : 80} justify="center"
          style={{ flexDirection: !screens.md || !screens.xl ? "column" : "row", width: "100%", marginBottom: "24px" }}
          data-testid="form-header-section">
          <Flex vertical gap={10} style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}>
            <Form.Item label="Receipt Code"><Input value={receiptCode} disabled /></Form.Item>
         <Form.Item
  name="rcv_date"
  label="Receipt Date"
  rules={[{ required: true, message: "Please select date" }]}
>
  <DatePicker
  style={{ width: "100%" }}
  onChange={(d) => {
    dispatch(setEntryDate(d ? d.toISOString() : null));
    form.setFieldValue("rcv_date", d);
  }}
  format={dateTimeFormat}
  disabledDate={disabledDate}
/>
</Form.Item>
            <Form.Item name="tags" label="Tags">
              <Select mode="multiple" maxTagCount="responsive" className="tag-multiselect" allowClear style={{ width: "100%" }}
                placeholder="Choose Tag Groups"
                options={tagGroupList.map((item) => ({ value: item.id, label: item.combinedField }))}
                onChange={(ids) => setSelectedTags(ids)}
                popupRender={(menu) => (
                  <>{menu}
                    <div style={{ display: "flex", justifyContent: "center", padding: 8, borderTop: "1px solid #f0f0f0" }}>
                      <Button type="link" onClick={() => setTagModal(true)}>+ Add New Tag</Button>
                    </div>
                  </>
                )}
              />
            </Form.Item>
          </Flex>
        </Flex>

        {/* ✅ Total received FIFO input */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <span style={{ fontWeight: 500 }}>
            {isSupplierReceiptMode ? "Total Received from Supplier:" : `Total Received (${symbol}):`}
          </span>
          <InputNumberField min={0} precision={2} step={1} stringMode style={{ width: 180 }}
            value={totalReceived} 
             onClick={() => {
              if (totalReceived === 0) {
                setTotalReceived("");
              }
            }} 
            onChange={(val) => distributePayment(val || 0)}
            placeholder="Enter total amount" />
          <span style={{ fontSize: 13, color: "#888" }}>Auto-distributes FIFO across rows below</span>
        </div>

        <Button type="dashed" onClick={handleAddRow} style={{ marginBottom: 12 }} data-testid="btn-add-row">
          + Add Row
        </Button>

        <Table
          dataSource={rows}
          columns={isSupplierReceiptMode ? supplierReceiptColumns : customerReceiptColumns}
          pagination={false}
          rowKey={(record) => `${record.key}-${record.customerKey}`}
          bordered
          scroll={{ x: "max-content" }}
          style={{ marginTop: 16, marginBottom: 24 }}
          footer={() => (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <strong>
                Total Amount: {symbol}{totalAmount.toFixed(2)}
                {!isSupplierReceiptMode && ` | Total Discount: ${symbol}${totalDiscount.toFixed(2)}`}
              </strong>
            </div>
          )}
        />

        <Form.Item name="description" label="Description">
          <TextArea rows={3} data-testid="description-input" />
        </Form.Item>

        <Space style={{ marginTop: 16 }} data-testid="footer-buttons">
          <Button onClick={handleCancel} data-testid="btn-cancel">Back</Button>
          <Button onClick={() => {
            form.resetFields();
            dispatch(resetReceipts());
            setSelectedCustomer(null);
            setSelectedSupplier(null);
            setSelectedTags([]);
            setRows([]);
            setTotalReceived(0);
            dispatch(fetchReceiptCode());
          }} data-testid="btn-reset">Reset</Button>
          {access_type == "WRITE" && (
            <Button type="primary" htmlType="submit" data-testid="btn-save">
              {isSupplierReceiptMode ? "Save Supplier Receipt" : "Save Receipt"}
            </Button>
          )}
        </Space>
      </Form>

      <AddNewModal open={tagModal} title="Add Tag" onClose={() => setTagModal(false)}>
        <TagCreateForm authInfo={authInfo}
          onSuccess={async ({ createdName }) => {
            const res = await dispatch(fetchTagGroupFields("")).unwrap();
            const match = res.find((t) => t?.raw?.field_name?.toLowerCase() === createdName.toLowerCase());
            if (!match) { message.warning("Tag creation failed or tag not found"); setTagModal(false); return; }
            setTagGroupList((prev) => {
              const exists = prev.some((p) => p.id === match.id);
              return exists ? prev : [...prev, { ...match.raw, combinedField: match.display_text, id: match.id }];
            });
            const current = form.getFieldValue("tags") || [];
            if (current.includes(match.id)) { message.info("Tag already exists and is already selected"); setTagModal(false); return; }
            form.setFieldsValue({ tags: [...current, match.id] });
            message.success("Tag added");
            setTagModal(false);
          }}
        />
      </AddNewModal>
    </div>
  );
};

export default CustomerReceipts;